import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { BaseView, BaseText } from '@/components/base'
import { AppIcons } from '@/components/AppIcons'
import { TransactionCurrencyAvatar } from '@/components/TransactionCurrencyAvatar'
import { useTheme } from '@/hooks/useTheme'
import { transactionServices } from '@/domain/transactions'
import { useUserStore } from '@/store/useUserStore'
import { HeaderProfileAndCryptoProps } from './types'
import { useAppPreferencesStore } from '@/store/useAppPreferencesStore'
import { formatFiatCurrency } from '@/utils/currency/formatFiatCurrency'

export const HeaderProfileAndCrypto: React.FC<HeaderProfileAndCryptoProps> = ({
  transaction,
  isLoading = false,
}) => {
  // All hooks MUST be called before any early returns
  const navigate = useNavigate()
  const { t } = useTranslation()
  const theme = useTheme()
  const { displayCurrency } = useAppPreferencesStore()
  const completeCurrencies = useUserStore(
    (state) => state.wallet?.completeCurrencies,
  )

  // Early return for loading state AFTER all hooks are called
  if (isLoading || !transaction) {
    return (
      <BaseView align="center" gap={24} py={32}>
        <BaseView
          circle
          circleSize={136}
          style={{
            backgroundColor: theme.colors.background.secondaryGray + '40',
          }}
        />
        <BaseView align="center" gap={8}>
          <BaseView
            style={{
              width: 200,
              height: 24,
              backgroundColor: theme.colors.background.secondaryGray + '40',
              borderRadius: 4,
            }}
          />
          <BaseView
            style={{
              width: 136,
              height: 16,
              backgroundColor: theme.colors.background.secondaryGray + '40',
              borderRadius: 4,
            }}
          />
        </BaseView>
      </BaseView>
    )
  }

  const { currency } =
    transactionServices.formatTransactionAmount(transaction)

  const isOutgoing = transaction.direction === 'send'
  const isPending = transaction.state === 'processing'
  const isFailed = ['declined', 'cancelled'].includes(transaction.state)
  const isRefunded = transaction.state === 'refunded'
  const isFailedOrRefunded = isFailed || isRefunded
  const isKemToKem = transaction.type === 'kem_to_kem'
  const isCardPurchase = transaction.type === 'card_write_off'
  const isCompleted = transaction.state === 'completed'

  const currencyDetails = completeCurrencies?.find(
    (curr) => curr.code === transaction.amount?.currency,
  )

  // Generate avatar data based on transaction type
  const getAvatarData = () => {
    switch (transaction.type) {
      case 'kem_to_kem':
      case 'on_ramp_send':
        return {
          type: 'user' as const,
          uri: transaction.subject_user?.avatar_path,
          name: transaction.subject_user?.first_name || 'U',
          backgroundColor: '#6366F1',
        }

      case 'card_write_off':
        return {
          type: 'icon' as const,
          icon: 'card' as const,
          backgroundColor: '#8B5CF6',
          iconSize: 64,
        }

      case 'on_ramp_top_up':
        return {
          type: 'icon' as const,
          icon: 'plus' as const,
          backgroundColor: '#10B981',
          iconSize: 64,
        }

      case 'external_withdraw':
        return {
          type: 'icon' as const,
          icon: 'upload' as const,
          backgroundColor: '#F59E0B',
          iconSize: 64,
        }

      case 'swap_sell':
        return {
          type: 'icon' as const,
          icon: 'exchange' as const,
          backgroundColor: '#EF4444',
          iconSize: 64,
        }

      case 'saving_transfer_in':
        return {
          type: 'icon' as const,
          icon: 'arrowDownCircle' as const,
          backgroundColor: '#4B6CFF',
          iconSize: 64,
        }

      case 'saving_transfer_out':
        return {
          type: 'icon' as const,
          icon: 'arrowRightCircle' as const,
          backgroundColor: '#000',
          iconSize: 64,
        }

      case 'kem_reward':
        return {
          type: 'icon' as const,
          icon: 'dollar' as const,
          backgroundColor: '#34C759',
          iconSize: 42,
        }

      case 'external_deposit':
        return {
          type: 'currency' as const,
          name: currency[0] || '?',
          backgroundColor: currencyDetails?.avatarBGRefColor || '#6B7280',
          uri: currencyDetails?.icon,
        }

      default:
        return {
          type: 'currency' as const,
          name: currency[0] || '?',
          backgroundColor: currencyDetails?.avatarBGRefColor || '#6B7280',
          uri: currencyDetails?.icon,
        }
    }
  }

  const avatarData = getAvatarData()

  const formatAmount = () => {
    let formattedAmount = transaction.amount?.amount || '0'

    // Format USDT with 2 decimals
    if (transaction.amount?.currency === 'USDT') {
      formattedAmount = Number(formattedAmount).toFixed(2)
    }

    return formattedAmount
  }

  const formatAmountText = () => {
    const formattedAmount = `${formatAmount()} ${currency}`

    if (isFailedOrRefunded) {
      return formattedAmount
    }

    // Add prefix for direction
    return (!isOutgoing ? '+ ' : '- ') + formattedAmount
  }

  const formatSubtitleAmount = () => {
    const priceForUnit = transaction.amount?.price_for_unit || '0'
    const formattedPrice = Number(priceForUnit).toFixed(2)
    const activeCurrencyCode = displayCurrency?.isin || 'USD'

    // Format balance exactly like in React Native
    const balanceFormatted = formatFiatCurrency({
      value: Number(formattedPrice),
      showDecimals: true,
      fiatCode: activeCurrencyCode,
    })
    return `${balanceFormatted} `
  }

  const renderSubjectNameOrNumber = () => {
    if (transaction.subject_user?.first_name) {
      return `${transaction.subject_user.first_name} ${transaction.subject_user.last_name || ''}`.trim()
    }

    if (transaction.subject_user?.phone_number) {
      return transaction.subject_user.phone_number
    }

    return t('translation:unnamed') || 'Unnamed'
  }

  const handleUserPress = () => {
    if (transaction.subject_user?.id) {
      navigate(`/user-profile/${transaction.subject_user.id}`)
    }
  }

  const canRenderSubtitle =
    transaction.amount?.price_for_unit && !isKemToKem && !isCardPurchase

  return (
    <BaseView gap={16}>
      {/* Avatar */}
      <BaseView align="center">
        {avatarData.type === 'currency' ? (
          <TransactionCurrencyAvatar
            uri={avatarData.uri}
            bgColor={avatarData.backgroundColor}
            size={136}
            iconSize={80}
            fallbackText={avatarData.name || currency}
            containerGradientProps={{
              locations: undefined,
              start: [1, 0],
              end: [0, 1],
            }}
          />
        ) : (
          <BaseView
            circle
            circleSize={136}
            center
            style={{
              backgroundColor: avatarData.backgroundColor,
              overflow: 'hidden',
            }}
          >
            {avatarData.type === 'user' && avatarData.uri ? (
              <img
                src={avatarData.uri}
                alt={avatarData.name || 'User'}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : avatarData.type === 'icon' && avatarData.icon ? (
              <AppIcons
                name={avatarData.icon}
                size={avatarData.iconSize || 64}
                color="#FFFFFF"
              />
            ) : (
              <BaseText fontSize={24} fontFamily="semibold" color="#FFFFFF">
                {avatarData.name || '?'}
              </BaseText>
            )}
          </BaseView>
        )}
      </BaseView>

      {/* User Info for kem_to_kem transactions */}
      {isKemToKem && (
        <BaseView align="center" gap={8}>
          <BaseText
            fontSize={28}
            fontFamily="semibold"
            color="label.default"
            textAlign="center"
          >
            {renderSubjectNameOrNumber()}
          </BaseText>

          {transaction.subject_user?.username && (
            <BaseView style={{ cursor: 'pointer' }} onClick={handleUserPress}>
              <BaseText
                fontSize={16}
                fontFamily="medium"
                color="label.clickable"
                textAlign="center"
              >
                @{transaction.subject_user.username}
              </BaseText>
            </BaseView>
          )}
        </BaseView>
      )}

      {/* Amount and Status */}
      <BaseView align="center" gap={16}>
        <BaseText
          fontSize={(() => {
            const text = formatAmountText()
            const len = text.length
            if (len <= 8) return 52
            if (len <= 10) return 44
            if (len <= 13) return 36
            if (len <= 16) return 28
            return 24
          })()}
          fontFamily="medium"
          style={{
            color: isFailedOrRefunded
              ? theme.colors.label.disabled
              : !isOutgoing && isCompleted
                ? theme.colors.label.default //set to default color as in RN
                : theme.colors.label.default,
            textDecorationLine: isFailedOrRefunded ? 'line-through' : 'none',
          }}
          textAlign="center"
        >
          {formatAmountText()}
        </BaseText>

        {/* Price per unit subtitle   REMOVED AS IN RN*/}
        {/* {canRenderSubtitle && (
          <BaseText
            fontSize={16}
            fontFamily="regular"
            color="label.description"
            textAlign="center"
          >
            {isOutgoing ? t('translation:at') || 'at' : t('translation:for') || 'for'}{' '}
            {formatSubtitleAmount()}
          </BaseText>
        )} */}

        {/* Pending Status */}
        {isPending && (
          <BaseView
            row
            align="center"
            gap={8}
            py={8}
            px={16}
            style={{
              backgroundColor: theme.colors.background.secondaryGray + '20',
              borderRadius: 20,
            }}
          >
            <AppIcons
              name="clock"
              size={16}
              color={theme.colors.label.description}
            />
            <BaseText
              fontSize={14}
              fontFamily="semibold"
              color="label.description"
            >
              {t('translation:pending') || 'Pending'}
            </BaseText>
          </BaseView>
        )}

        {/* Failed/Refunded Status */}
        {isFailedOrRefunded && (
          <BaseView
            row
            align="center"
            gap={8}
            py={8}
            px={16}
            style={{
              backgroundColor: theme.colors.background.secondaryGray + '20',
              borderRadius: 20,
            }}
          >
            <AppIcons
              name="triangleWarning"
              size={16}
              color={theme.colors.label.flagLosses}
            />
            <BaseText
              fontSize={14}
              fontFamily="semibold"
              color="label.flagLoss"
            >
              {isFailed
                ? t('transactions:failed') || 'Failed'
                : t('transactions:refunded') || 'Refunded'}
            </BaseText>
          </BaseView>
        )}
      </BaseView>
    </BaseView>
  )
}
